import { useCallback, useEffect, useState } from "react";
import { LIVE_INTERVAL_MS, useVisibleInterval } from "../lib/polling";
import {
  Box, Stack, Typography, Tabs, Tab, Paper, Card, CardContent, IconButton, Tooltip,
  FormControlLabel, Switch, TextField, MenuItem, ToggleButton, ToggleButtonGroup,
  CircularProgress, Alert, Button, Chip,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import { LineChart } from "@mui/x-charts/LineChart";
import { BRAND } from "../styles/theme";
import {
  getApiCallTotals, getApiCallsByEndpoint, fmtArTime, fmtArDateTime,
  arLocalToEpoch, epochToArLocal,
  type ApiTotals, type ApiByEndpoint, type Granularity,
} from "../services/monitoring";

const SERIES_COLORS: Record<string, string> = {
  cmf:           BRAND.primary,
  coelsa:        "#0FA958",
  coelsa_in:     "#F59E0B",
  cmf_transfers: "#6B7280",
};
const ENDPOINT_PALETTE = [
  "#5B2FEB", "#0FA958", "#F59E0B", "#EF4444", "#06B6D4", "#EC4899", "#8B5CF6", "#84CC16", "#6B7280",
];

const LIVE_WINDOW_MIN  = 60;

const GRAN_LABEL: Record<Granularity, string> = {
  minute: "por minuto", hour: "por hora", day: "por día",
};
const GRAN_UNIT: Record<Granularity, string> = {
  minute: "minuto", hour: "hora", day: "día",
};
const GRAN_SECONDS: Record<Granularity, number> = {
  minute: 60, hour: 3600, day: 86400,
};

/** Cantidad de buckets (intervalos) en el rango, según la granularidad. */
function bucketCount(fromIso: string, toIso: string, gran: Granularity): number {
  const secs = (new Date(toIso).getTime() - new Date(fromIso).getTime()) / 1000;
  return Math.max(1, Math.round(secs / GRAN_SECONDS[gran]));
}

/** Encabezado explícito: rango (hora AR) + granularidad. Evita ambigüedad. */
function RangeHeader({ from, to, granularity }: { from: string; to: string; granularity: Granularity }) {
  return (
    <Alert severity="info" variant="outlined" icon={false} sx={{ py: 0.5 }}>
      <Typography variant="body2">
        <strong>Rango:</strong> {fmtArDateTime(from)} — {fmtArDateTime(to)} (hora Argentina)
        {"  ·  "}
        <strong>Granularidad:</strong> {GRAN_LABEL[granularity]}
        {"  ·  "}
        <span style={{ color: "#6B7280" }}>cada punto del gráfico = llamadas {GRAN_LABEL[granularity]}; la tabla suma todo el rango</span>
      </Typography>
    </Alert>
  );
}

// ============================ Página ============================
export default function Monitoreo() {
  const [tab, setTab] = useState<"live" | "history">("live");
  return (
    <Box sx={{ p: 3, height: "100%", overflow: "auto" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5">Monitoreo de APIs CMF / Coelsa</Typography>
          <Typography variant="body2" color="text.secondary">
            Llamadas a las APIs externas. Cada llamada = una invocación del conector. Horarios en hora Argentina.
          </Typography>
        </Box>
      </Stack>
      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab value="live" label="En vivo" />
        <Tab value="history" label="Histórico" />
      </Tabs>
      {tab === "live" ? <LiveView /> : <HistoryView />}
    </Box>
  );
}

// ============================ En vivo ============================
function LiveView() {
  const [data, setData] = useState<ApiTotals | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [auto, setAuto] = useState(true);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const now = Math.floor(Date.now() / 1000);
      const d = await getApiCallTotals({
        from: now - LIVE_WINDOW_MIN * 60, to: now, granularity: "minute",
      });
      setData(d);
      setUpdatedAt(new Date().toISOString());
    } catch (e: any) {
      setErr(e?.message ?? "Error al cargar");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useVisibleInterval(load, LIVE_INTERVAL_MS, auto);

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
        <FormControlLabel
          control={<Switch checked={auto} onChange={(e) => setAuto(e.target.checked)} />}
          label={`Auto-refresh (${LIVE_INTERVAL_MS / 1000}s)`}
        />
        <Tooltip title="Refrescar ahora">
          <span><IconButton onClick={load} disabled={loading}><RefreshIcon /></IconButton></span>
        </Tooltip>
        {loading && <CircularProgress size={18} />}
        {updatedAt && (
          <Typography variant="caption" color="text.secondary">
            Última actualización: {fmtArDateTime(updatedAt)}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary">
          Ventana: últimos {LIVE_WINDOW_MIN} min · granularidad 1 min
        </Typography>
      </Stack>

      {err && <Alert severity="error">{err}</Alert>}

      {/* Tarjetas con la tasa del último minuto cerrado */}
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        {data?.series.map((s) => {
          // último minuto cerrado = anteúltimo punto (el último puede estar incompleto)
          const pts = s.points;
          const rate = pts.length >= 2 ? pts[pts.length - 2].v : (pts[pts.length - 1]?.v ?? 0);
          return (
            <Card key={s.key} variant="outlined" sx={{ minWidth: 190, flex: "1 1 190px" }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: SERIES_COLORS[s.key] }} />
                  <Typography variant="body2" noWrap>{s.label}</Typography>
                </Stack>
                <Typography variant="h4" sx={{ mt: 1 }}>{rate}<Typography component="span" variant="body2" color="text.secondary"> /min</Typography></Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">{s.total} en 60 min</Typography>
                  {s.direction === "inbound" && <Chip size="small" label="entrante" color="warning" variant="outlined" />}
                </Stack>
              </CardContent>
            </Card>
          );
        })}
      </Stack>

      {data && <TotalsChart data={data} />}
    </Stack>
  );
}

// ============================ Histórico ============================
function HistoryView() {
  const now = Math.floor(Date.now() / 1000);
  const [fromLocal, setFromLocal] = useState(() => epochToArLocal(now - 3 * 3600));
  const [toLocal, setToLocal]     = useState(() => epochToArLocal(now));
  const [granularity, setGranularity] = useState<Granularity>("hour");
  const [byEndpoint, setByEndpoint]   = useState(false);
  const [service, setService] = useState<"cmf" | "coelsa">("cmf");

  const [totals, setTotals] = useState<ApiTotals | null>(null);
  const [breakdown, setBreakdown] = useState<ApiByEndpoint | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    const from = arLocalToEpoch(fromLocal);
    const to   = arLocalToEpoch(toLocal);
    try {
      if (byEndpoint) {
        setBreakdown(await getApiCallsByEndpoint({ service, from, to, granularity }));
        setTotals(null);
      } else {
        setTotals(await getApiCallTotals({ from, to, granularity }));
        setBreakdown(null);
      }
    } catch (e: any) {
      setErr(e?.message ?? "Error al cargar");
    } finally {
      setLoading(false);
    }
  }, [fromLocal, toLocal, granularity, byEndpoint, service]);

  useEffect(() => { load(); }, [load]);

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
          <TextField
            label="Desde (hora AR)" type="datetime-local" size="small" sx={{ maxWidth: 230 }}
            InputLabelProps={{ shrink: true }}
            value={fromLocal} onChange={(e) => setFromLocal(e.target.value)}
          />
          <TextField
            label="Hasta (hora AR)" type="datetime-local" size="small" sx={{ maxWidth: 230 }}
            InputLabelProps={{ shrink: true }}
            value={toLocal} onChange={(e) => setToLocal(e.target.value)}
          />
          <ToggleButtonGroup
            exclusive size="small" value={granularity}
            onChange={(_e, v) => v && setGranularity(v)}
          >
            <ToggleButton value="minute">Minuto</ToggleButton>
            <ToggleButton value="hour">Hora</ToggleButton>
            <ToggleButton value="day">Día</ToggleButton>
          </ToggleButtonGroup>
          <FormControlLabel
            control={<Switch checked={byEndpoint} onChange={(e) => setByEndpoint(e.target.checked)} />}
            label="Desglose por endpoint"
          />
          {byEndpoint && (
            <TextField
              select label="Servicio" size="small" sx={{ maxWidth: 160 }}
              value={service} onChange={(e) => setService(e.target.value as "cmf" | "coelsa")}
            >
              <MenuItem value="cmf">CMF</MenuItem>
              <MenuItem value="coelsa">Coelsa</MenuItem>
            </TextField>
          )}
          <Tooltip title="Refrescar">
            <span><IconButton onClick={load} disabled={loading}><RefreshIcon /></IconButton></span>
          </Tooltip>
          {loading && <CircularProgress size={18} />}
        </Stack>
        {byEndpoint && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            El desglose usa CloudWatch Logs Insights (más lento, máx 7 días de rango).
          </Typography>
        )}
      </Paper>

      {err && <Alert severity="error">{err}</Alert>}

      {!byEndpoint && totals && (
        <>
          <RangeHeader from={totals.from} to={totals.to} granularity={totals.granularity} />
          <TotalsChart data={totals} />
          <TotalsTable data={totals} />
        </>
      )}
      {byEndpoint && breakdown && (
        <>
          <RangeHeader from={breakdown.from} to={breakdown.to} granularity={breakdown.granularity} />
          <BreakdownView data={breakdown} />
        </>
      )}
    </Stack>
  );
}

// ============================ Gráficos / tablas ============================
function TotalsChart({ data }: { data: ApiTotals }) {
  const [drill, setDrill] = useState<{ bucketIso: string; service: "cmf" | "coelsa" } | null>(null);

  // Eje X común: usar los timestamps de la serie más larga.
  const longest = data.series.reduce((a, b) => (b.points.length > a.points.length ? b : a), data.series[0]);
  const xLabels = longest?.points.map((p) => p.t) ?? [];
  const idx = new Map(xLabels.map((t, i) => [t, i]));
  const seriesCfg = data.series
    .filter((s) => s.total > 0 || s.key === "cmf" || s.key === "coelsa")
    .map((s) => {
      const arr = new Array(xLabels.length).fill(0);
      for (const p of s.points) { const i = idx.get(p.t); if (i != null) arr[i] = p.v; }
      return { id: s.key, data: arr, label: s.label, color: SERIES_COLORS[s.key], showMark: false, curve: "linear" as const };
    });

  // Click en el gráfico → drill-down del bucket. Solo CMF/Coelsa tienen desglose
  // por endpoint; elegimos el servicio con más llamadas en ese bucket.
  const onAxisClick = (_e: unknown, p: { dataIndex: number; seriesValues?: Record<string, number | null> } | null) => {
    if (!p || p.dataIndex == null) return;
    const bucketIso = xLabels[p.dataIndex];
    if (!bucketIso) return;
    const sv = p.seriesValues ?? {};
    const cmfV = Number(sv["cmf"] ?? 0), coelsaV = Number(sv["coelsa"] ?? 0);
    if (cmfV === 0 && coelsaV === 0) {
      setDrill({ bucketIso, service: "coelsa" }); // mostrará "sin llamadas" si no hubo
      return;
    }
    setDrill({ bucketIso, service: coelsaV >= cmfV ? "coelsa" : "cmf" });
  };

  if (!xLabels.length) return <Alert severity="info">Sin datos en el rango seleccionado.</Alert>;
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="caption" color="text.secondary">
        Tip: hacé click en un punto del gráfico para ver qué endpoints (CMF/Coelsa) lo componen.
      </Typography>
      <LineChart
        height={320}
        xAxis={[{ data: xLabels, scaleType: "point",
          valueFormatter: (t: string) => fmtArTime(t, data.granularity) }]}
        series={seriesCfg}
        onAxisClick={onAxisClick as any}
        slotProps={{ legend: { direction: "row", position: { vertical: "top", horizontal: "middle" } } }}
      />
      {drill && (
        <BucketDrilldown
          bucketIso={drill.bucketIso}
          service={drill.service}
          granularity={data.granularity}
          onClose={() => setDrill(null)}
          onSwitchService={(svc) => setDrill({ bucketIso: drill.bucketIso, service: svc })}
        />
      )}
    </Paper>
  );
}

// Detalle de un bucket: corre Logs Insights solo para ese intervalo y lista los
// endpoints. Permite alternar entre CMF y Coelsa.
function BucketDrilldown({ bucketIso, service, granularity, onClose, onSwitchService }: {
  bucketIso: string;
  service: "cmf" | "coelsa";
  granularity: Granularity;
  onClose: () => void;
  onSwitchService: (svc: "cmf" | "coelsa") => void;
}) {
  const [rows, setRows] = useState<{ endpoint: string; total: number }[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const from = Math.floor(new Date(bucketIso).getTime() / 1000);
  const to   = from + GRAN_SECONDS[granularity];

  useEffect(() => {
    let alive = true;
    setLoading(true); setErr(null); setRows(null);
    getApiCallsByEndpoint({ service, from, to, granularity })
      .then((d) => { if (alive) setRows(d.endpoints.map((e) => ({ endpoint: e.endpoint, total: e.total }))); })
      .catch((e) => { if (alive) setErr(e?.message ?? "Error al cargar"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [service, from, to, granularity]);

  const total = rows?.reduce((s, r) => s + r.total, 0) ?? 0;

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        Detalle — {fmtArTime(bucketIso, granularity)} ({GRAN_LABEL[granularity].replace("por ", "")})
      </DialogTitle>
      <DialogContent>
        <ToggleButtonGroup
          exclusive size="small" value={service} sx={{ mb: 1.5 }}
          onChange={(_e, v) => v && onSwitchService(v)}
        >
          <ToggleButton value="cmf">CMF</ToggleButton>
          <ToggleButton value="coelsa">Coelsa</ToggleButton>
        </ToggleButtonGroup>
        {loading && <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={18} /><Typography variant="body2">Consultando logs…</Typography></Stack>}
        {err && <Alert severity="error">{err}</Alert>}
        {rows && rows.length === 0 && !loading && (
          <Alert severity="info">Sin llamadas a {service.toUpperCase()} en este intervalo.</Alert>
        )}
        {rows && rows.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Endpoint</TableCell>
                <TableCell align="right">Llamadas</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.endpoint}>
                  <TableCell>{r.endpoint}</TableCell>
                  <TableCell align="right">{r.total.toLocaleString("es-AR")}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{total.toLocaleString("es-AR")}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}

function TotalsTable({ data }: { data: ApiTotals }) {
  const buckets = bucketCount(data.from, data.to, data.granularity);
  const unit = GRAN_UNIT[data.granularity];
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Serie</TableCell>
            <TableCell>Dirección</TableCell>
            <TableCell align="right">Total en el rango</TableCell>
            <TableCell align="right">Promedio por {unit}</TableCell>
            <TableCell align="right">Máximo en 1 {unit}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.series.map((s) => {
            const max = s.points.reduce((m, p) => Math.max(m, p.v), 0);
            const avg = s.total / buckets;
            return (
              <TableRow key={s.key}>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: SERIES_COLORS[s.key] }} />
                    {s.label}
                  </Stack>
                </TableCell>
                <TableCell>{s.direction === "inbound" ? "entrante" : "saliente"}</TableCell>
                <TableCell align="right">{s.total.toLocaleString("es-AR")}</TableCell>
                <TableCell align="right">{avg.toLocaleString("es-AR", { maximumFractionDigits: 1 })}</TableCell>
                <TableCell align="right">{max.toLocaleString("es-AR")}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function BreakdownView({ data }: { data: ApiByEndpoint }) {
  const xLabels = data.buckets;
  const seriesCfg = data.endpoints.map((ep, i) => ({
    data: ep.points.map((p) => p.v),
    label: ep.endpoint,
    color: ENDPOINT_PALETTE[i % ENDPOINT_PALETTE.length],
    showMark: false,
    curve: "linear" as const,
  }));

  const exportCsv = () => {
    const header = ["timestamp_utc", ...data.endpoints.map((e) => e.endpoint)].join(",");
    const lines = xLabels.map((b, i) =>
      [b, ...data.endpoints.map((e) => e.points[i]?.v ?? 0)].join(","));
    const blob = new Blob([[header, ...lines].join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `api-calls-${data.service}-${data.granularity}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  if (!xLabels.length) return <Alert severity="info">Sin llamadas en el rango seleccionado.</Alert>;
  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <LineChart
          height={340}
          xAxis={[{ data: xLabels, scaleType: "point",
            valueFormatter: (t: string) => fmtArTime(t, data.granularity) }]}
          series={seriesCfg}
          slotProps={{ legend: { direction: "row", position: { vertical: "top", horizontal: "middle" } } }}
        />
      </Paper>
      <Stack direction="row" justifyContent="flex-end">
        <Button startIcon={<DownloadIcon />} onClick={exportCsv} size="small">Exportar CSV</Button>
      </Stack>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Endpoint</TableCell>
              <TableCell align="right">Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.endpoints.map((ep, i) => (
              <TableRow key={ep.endpoint}>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ width: 10, height: 10, borderRadius: "50%",
                      bgcolor: ENDPOINT_PALETTE[i % ENDPOINT_PALETTE.length] }} />
                    {ep.endpoint}
                  </Stack>
                </TableCell>
                <TableCell align="right">{ep.total.toLocaleString("es-AR")}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}
